import localforage from "localforage";
import {
  cancelEnrollment,
  fetchBillingReadiness,
  fetchCurrentEnrollment,
  newIdempotencyKey,
  PIX_AUTOMATICO_READINESS_CHANGED_EVENT,
  startEnrollment,
  syncBillingProfile,
  updateWeeklyLimit,
  UNKNOWN_CANCELLATION_ELIGIBILITY,
  type CancellationEligibility,
  type PixAutomaticoFailure,
  type PixEnrollment,
  type PixProcessingFee,
  type StartEnrollmentInput,
  type WeeklyLimit,
} from "../../services/pixAutomatico";
import { formatBRL } from "../financial/InvoiceSplitPreviewModal";
import {
  actionFailureNotice,
  WEEKLY_LIMIT_CONFLICT_RELOADED,
  WEEKLY_LIMIT_CONFLICT_STALE,
  isTransientFailure,
  isUncertainOutcome,
  loadErrorView,
  nextPollDelay,
  POLL_INTERVAL_MS,
  readinessContext,
  shouldPoll,
  START_WAITS_FOR_FEE,
  CANCEL_OUTSTANDING_SPEND,
  CANCEL_ELIGIBILITY_UNKNOWN,
  viewForEnrollment,
  weeklyLimitFromReadiness,
  type PixAutomaticoNotice,
  type PixAutomaticoView,
  type ReadinessContext,
  type StoredWeeklyLimit,
} from "./pixAutomaticoState";

export type PixAutomaticoPanelState = {
  view: PixAutomaticoView;
  weeklyLimit: StoredWeeklyLimit | null;
  cancellationEligibility: CancellationEligibility;
  /** Payments accepts new authorizations (its readiness `activationEnabled`). */
  activationEnabled: boolean;
  /** Payments enforces the weekly limit (see ReadinessContext). */
  weeklyAdmissionActive: boolean;
  /**
   * The Pix processing fee Payments reported on its last readiness answer;
   * null until one was read. The panel shows it and offers a start only when
   * it is `reviewed`.
   */
  processingFee: PixProcessingFee | null;
  minimumChargeCents?: number;
  limitBelowMinimum?: boolean;
  notice: PixAutomaticoNotice | null;
  /** A poll failed and a later one is scheduled; the screen may be behind. */
  pollingIssue: boolean;
  starting: boolean;
  cancelling: boolean;
  savingLimit: boolean;
};

export const INITIAL_PIX_AUTOMATICO_STATE: PixAutomaticoPanelState = {
  view: { kind: "loading" },
  weeklyLimit: null,
  cancellationEligibility: UNKNOWN_CANCELLATION_ELIGIBILITY,
  activationEnabled: true,
  weeklyAdmissionActive: false,
  processingFee: null,
  notice: null,
  pollingIssue: false,
  starting: false,
  cancelling: false,
  savingLimit: false,
};

export type PixAutomaticoControllerDeps = {
  onChange: (state: PixAutomaticoPanelState) => void;
  termsVersion: () => string;
  /** Tells the shell to re-read the payment readiness. Defaults to a window event. */
  announceReadinessChange?: () => void;
};

export type PixAutomaticoController = {
  mount: () => void;
  unmount: () => void;
  start: (weeklyLimit: WeeklyLimit) => Promise<void>;
  cancel: () => Promise<void>;
  saveWeeklyLimit: (weeklyLimit: WeeklyLimit) => Promise<boolean>;
  retry: () => Promise<void>;
  dismissNotice: () => void;
  getState: () => PixAutomaticoPanelState;
};

async function readJwt(): Promise<string | null> {
  const jwt = await localforage.getItem("JWT");
  return typeof jwt === "string" && jwt !== "" ? jwt : null;
}

function dispatchReadinessChange() {
  window.dispatchEvent(new Event(PIX_AUTOMATICO_READINESS_CHANGED_EVENT));
}

const UNAUTHORIZED: PixAutomaticoFailure = { code: "unauthorized" };

/**
 * After Enterprise republishes the company profile, the same start is retried
 * on this schedule while the profile crosses the outbox into Payments.
 */
export const PROFILE_SYNC_RETRY_DELAYS_MS = [1500, 3000, 5000, 8000];

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

type BusyFlag = "starting" | "cancelling" | "savingLimit";

/**
 * Owns the Pix Automatico page outside React, so its polling and races can be
 * tested with fake timers alone.
 *
 * Two rules hold everything together:
 * - `generation` changes whenever an answer becomes authoritative (a load, an
 *   action, an unmount). A response started under an older generation is
 *   dropped, so a slow poll can never overwrite what an action just returned,
 *   and nothing lands after the page left.
 * - Polling runs only while the screen is one the provider is still deciding
 *   (see `shouldPoll`), one request at a time, and stops on unmount.
 */
export function createPixAutomaticoController(
  deps: PixAutomaticoControllerDeps,
): PixAutomaticoController {
  const announce = deps.announceReadinessChange ?? dispatchReadinessChange;
  let state = INITIAL_PIX_AUTOMATICO_STATE;
  let mounted = false;
  let generation = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let consecutivePollFailures = 0;
  let enrollment: PixEnrollment | null = null;
  // Replaced by every successful readiness read. Before the first one no
  // enrollment is mapped (the screen is loading or an error), and this value
  // never calls an approval confirmed.
  let context: ReadinessContext = {
    activationEnabled: true,
    authorizationReady: false,
    weeklyAdmissionActive: false,
  };
  // A replay key owns the original body, including the accepted terms. Later
  // runtime config or caller mutations cannot change an uncertain request.
  let pendingStart: { input: StartEnrollmentInput; weeklyLimitKey: string } | null = null;
  // An old reviewed enrollment can remain the Current answer while a newly
  // submitted Start has an uncertain transport outcome. Seeing the same
  // resolution again must not erase that new Start's replay key.
  let reviewedResolutionID: string | null = null;
  let busy = false;

  function set(next: Partial<PixAutomaticoPanelState>) {
    state = { ...state, ...next };
    if (mounted) deps.onChange(state);
  }

  function invalidateCancellationEligibility() {
    if (enrollment) {
      enrollment = { ...enrollment, cancellationEligibility: UNKNOWN_CANCELLATION_ELIGIBILITY };
    }
    set({ cancellationEligibility: UNKNOWN_CANCELLATION_ELIGIBILITY });
  }

  function stopPolling() {
    if (timer !== undefined) clearTimeout(timer);
    timer = undefined;
  }

  function schedulePoll(delay: number) {
    stopPolling();
    if (!mounted) return;
    timer = setTimeout(() => {
      timer = undefined;
      void poll();
    }, delay);
  }

  function resumePolling() {
    if (shouldPoll(state.view) && timer === undefined) schedulePoll(POLL_INTERVAL_MS);
  }

  function applyEnrollment(
    next: PixEnrollment | null,
    extra: Partial<PixAutomaticoPanelState> = {},
    fromCurrent = false,
  ) {
    enrollment = next;
    consecutivePollFailures = 0;
    const view = viewForEnrollment(next, context);
    // The first authoritative GET of this exact audited resolution ends the
    // old uncertain POST's replay path. Re-reading the same old row after a
    // fresh Start is uncertain must keep that new Start's replay key.
    if (fromCurrent && next && view.kind === "reviewed_retry" && next.id !== reviewedResolutionID) {
      reviewedResolutionID = next.id;
      pendingStart = null;
    }
    set({
      view,
      cancellationEligibility: next?.cancellationEligibility ?? UNKNOWN_CANCELLATION_ELIGIBILITY,
      pollingIssue: false,
      ...extra,
    });
    if (shouldPoll(view)) schedulePoll(POLL_INTERVAL_MS);
    else stopPolling();
  }

  async function load(notice: PixAutomaticoNotice | null = null) {
    stopPolling();
    invalidateCancellationEligibility();
    const gen = ++generation;
    consecutivePollFailures = 0;
    set({ view: { kind: "loading" }, notice: null, pollingIssue: false });
    const jwt = await readJwt();
    if (gen !== generation) return;
    if (!jwt) {
      set({ view: loadErrorView(UNAUTHORIZED), notice });
      return;
    }
    const [readiness, current] = await Promise.all([
      fetchBillingReadiness(jwt),
      fetchCurrentEnrollment(jwt),
    ]);
    if (gen !== generation) return;
    if (!readiness.ok) {
      set({ view: loadErrorView(readiness.failure), notice });
      return;
    }
    context = readinessContext(readiness.value);
    const weeklyLimit = weeklyLimitFromReadiness(readiness.value);
    const processingFee = readiness.value.pixProcessingFee;
    const { activationEnabled, weeklyAdmissionActive } = context;
    if (!current.ok) {
      set({
        view: loadErrorView(current.failure),
        weeklyLimit,
        activationEnabled,
        weeklyAdmissionActive,
        processingFee,
        minimumChargeCents: readiness.value.minimumChargeCents,
        limitBelowMinimum: readiness.value.reasons.includes("limit_below_minimum"),
        notice,
      });
      return;
    }
    // With the activation off, an existing authorization is still shown as
    // it is (and can be cancelled); only a company without one sees the
    // activation screen (see viewForEnrollment).
    applyEnrollment(
      current.value,
      {
        weeklyLimit,
        activationEnabled,
        weeklyAdmissionActive,
        processingFee,
        minimumChargeCents: readiness.value.minimumChargeCents,
        limitBelowMinimum: readiness.value.reasons.includes("limit_below_minimum"),
        notice,
      },
      true,
    );
  }

  /** Reads readiness again; false when it could not be read (nothing changes then). */
  async function refreshReadiness(jwt: string): Promise<boolean> {
    const readiness = await fetchBillingReadiness(jwt);
    if (!readiness.ok || !mounted) return false;
    context = readinessContext(readiness.value);
    set({
      weeklyLimit: weeklyLimitFromReadiness(readiness.value),
      activationEnabled: context.activationEnabled,
      weeklyAdmissionActive: context.weeklyAdmissionActive,
      processingFee: readiness.value.pixProcessingFee,
      minimumChargeCents: readiness.value.minimumChargeCents,
      limitBelowMinimum: readiness.value.reasons.includes("limit_below_minimum"),
    });
    return true;
  }

  async function poll() {
    const gen = generation;
    const jwt = await readJwt();
    if (gen !== generation || !mounted) return;
    if (!jwt) {
      invalidateCancellationEligibility();
      set({ view: loadErrorView(UNAUTHORIZED), pollingIssue: false });
      return;
    }
    const result = await fetchCurrentEnrollment(jwt);
    if (gen !== generation || !mounted) return;
    if (!result.ok) {
      invalidateCancellationEligibility();
      if (isTransientFailure(result.failure.code)) {
        consecutivePollFailures += 1;
        set({ pollingIssue: true });
        schedulePoll(nextPollDelay(consecutivePollFailures));
      } else {
        set({ view: loadErrorView(result.failure), pollingIssue: false });
      }
      return;
    }
    if (!shouldPoll(viewForEnrollment(result.value, context))) {
      // Settled: Payments' readiness decides whether an approval counts yet.
      const read = await refreshReadiness(jwt);
      if (gen !== generation || !mounted) return;
      // An answer from before the change never confirms an approval.
      if (!read) context = { ...context, authorizationReady: false };
    }
    applyEnrollment(result.value, {}, true);
    // The authorization settled (approved, refused, expired, cancelled...):
    // readiness moved with it, so the shell must re-read it.
    if (!shouldPoll(state.view)) announce();
  }

  function busyPatch(flag: BusyFlag, value: boolean): Partial<PixAutomaticoPanelState> {
    switch (flag) {
      case "starting":
        return { starting: value };
      case "cancelling":
        return { cancelling: value };
      case "savingLimit":
        return { savingLimit: value };
      // BusyFlag has exactly these three members.
      // no default
    }
  }

  /** One action at a time: a double click can never send two requests. */
  async function exclusive<T>(flag: BusyFlag, fallback: T, run: () => Promise<T>): Promise<T> {
    if (busy) return fallback;
    busy = true;
    set({ ...busyPatch(flag, true), notice: null });
    try {
      return await run();
    } finally {
      busy = false;
      set(busyPatch(flag, false));
    }
  }

  async function start(weeklyLimit: WeeklyLimit) {
    await exclusive("starting", undefined, async () => {
      // Only on a fee Payments reported as reviewed, the one the company was
      // shown. The panel offers no start otherwise; this keeps it that way.
      const fee = state.processingFee;
      if (fee?.state !== "reviewed") {
        set({
          notice: {
            tone: "info",
            message: START_WAITS_FOR_FEE,
          },
        });
        return;
      }
      const chosenLimit = { ...weeklyLimit };
      const weeklyLimitKey =
        chosenLimit.mode === "fixed" ? `fixed:${chosenLimit.cents}` : chosenLimit.mode;
      if (pendingStart && pendingStart.weeklyLimitKey !== weeklyLimitKey) {
        set({
          notice: {
            tone: "warning",
            message:
              "Seu pedido de autorização ainda não foi confirmado. Atualize o estado antes de alterar o limite e fazer outro pedido.",
          },
        });
        return;
      }
      const chosenTermsVersion = pendingStart?.input.termsVersion ?? deps.termsVersion();
      const jwt = await readJwt();
      if (!jwt) {
        set({ notice: actionFailureNotice("start", UNAUTHORIZED) });
        return;
      }
      if (!pendingStart) {
        let key: string;
        try {
          key = newIdempotencyKey();
        } catch {
          set({
            notice: {
              tone: "error",
              message:
                "Este navegador não oferece o recurso de segurança necessário para autorizar. Atualize o navegador e tente de novo.",
            },
          });
          return;
        }
        pendingStart = {
          weeklyLimitKey,
          input: {
            idempotencyKey: key,
            termsVersion: chosenTermsVersion,
            weeklyLimit: chosenLimit,
          },
        };
      }
      const gen = ++generation;
      stopPolling();
      const input = pendingStart.input;
      const attempt = () =>
        startEnrollment(jwt, { ...input, weeklyLimit: { ...input.weeklyLimit } });
      let result = await attempt();
      if (gen !== generation || !mounted) return;
      let profileView: Extract<PixAutomaticoView, { kind: "profile_incomplete" }> = {
        kind: "profile_incomplete",
      };
      if (!result.ok && result.failure.code === "profile_incomplete") {
        // Organizations older than the Payments fiscal profile never sent one.
        // Ask Enterprise to publish it (its ADR 0009) and retry this same
        // start, same key and weekly limit, while the profile crosses the
        // outbox. Only a field the company must fill in reaches the screen.
        const sync = await syncBillingProfile(jwt);
        if (gen !== generation || !mounted) return;
        if (sync.kind === "sent") {
          for (const delay of PROFILE_SYNC_RETRY_DELAYS_MS) {
            await wait(delay);
            if (gen !== generation || !mounted) return;
            result = await attempt();
            if (gen !== generation || !mounted) return;
            if (result.ok || result.failure.code !== "profile_incomplete") break;
          }
        } else if (sync.kind === "missing") {
          profileView = { kind: "profile_incomplete", missing: sync.fields };
        } else {
          profileView = { kind: "profile_incomplete", registryUnavailable: true };
        }
      }
      if (result.ok) {
        pendingStart = null;
        applyEnrollment(result.value);
        // Starting creates the weekly policy when none existed yet.
        await refreshReadiness(jwt);
        announce();
        return;
      }
      const { code } = result.failure;
      if (!isUncertainOutcome(code)) pendingStart = null;
      if (code === "profile_incomplete") {
        set({ view: profileView });
        await keepChosenWeeklyLimit(jwt, input.weeklyLimit);
      } else if (code === "activation_disabled") {
        // Keep whatever the company already has on screen (a refused or
        // expired authorization, say); only the start goes away.
        context = { ...context, activationEnabled: false };
        applyEnrollment(enrollment, { activationEnabled: false });
      } else if (code === "state_conflict" || isUncertainOutcome(code)) {
        await load(actionFailureNotice("start", result.failure));
      } else {
        set({ notice: actionFailureNotice("start", result.failure) });
        resumePolling();
      }
    });
  }

  /**
   * A start refused before the authorization existed must not lose the weekly
   * limit chosen with it: when no policy exists yet it is stored as the first
   * one, so the screen shows it and the next start sends the same choice.
   */
  async function keepChosenWeeklyLimit(jwt: string, weeklyLimit: WeeklyLimit) {
    if (state.weeklyLimit) return;
    const saved = await updateWeeklyLimit(jwt, { weeklyLimit, expectedVersion: 0 });
    if (!mounted || !saved.ok) return;
    set({ weeklyLimit: { ...saved.value.weeklyLimit, version: saved.value.version } });
  }

  async function cancel() {
    await exclusive("cancelling", undefined, async () => {
      const target = enrollment;
      if (!target) return;
      if (target.cancellationEligibility?.state !== "allowed") {
        set({
          notice: {
            tone: "warning",
            message:
              target.cancellationEligibility?.state === "blocked"
                ? CANCEL_OUTSTANDING_SPEND
                : CANCEL_ELIGIBILITY_UNKNOWN,
          },
        });
        return;
      }
      const actionGeneration = generation;
      const targetId = target.id;
      const jwt = await readJwt();
      // A poll can replace the decision while storage is reading the token;
      // a reload can invalidate it before its own answer arrives. Only the
      // current affirmative decision for the clicked enrollment can cancel.
      if (actionGeneration !== generation || !mounted) return;
      const current = enrollment;
      if (
        !current ||
        current.id !== targetId ||
        current.cancellationEligibility?.state !== "allowed"
      ) {
        set({
          notice: {
            tone: "warning",
            message:
              current?.id === targetId && current.cancellationEligibility?.state === "blocked"
                ? CANCEL_OUTSTANDING_SPEND
                : CANCEL_ELIGIBILITY_UNKNOWN,
          },
        });
        return;
      }
      if (!jwt) {
        set({ notice: actionFailureNotice("cancel", UNAUTHORIZED) });
        return;
      }
      const gen = ++generation;
      stopPolling();
      const result = await cancelEnrollment(jwt, targetId);
      if (gen !== generation || !mounted) return;
      if (result.ok) {
        applyEnrollment(result.value);
        announce();
        return;
      }
      const { code } = result.failure;
      if (
        code === "confirmed_spend_outstanding" ||
        code === "state_conflict" ||
        code === "not_found" ||
        isUncertainOutcome(code)
      ) {
        await load(actionFailureNotice("cancel", result.failure));
      } else {
        set({ notice: actionFailureNotice("cancel", result.failure) });
        resumePolling();
      }
    });
  }

  async function saveWeeklyLimit(weeklyLimit: WeeklyLimit): Promise<boolean> {
    return exclusive("savingLimit", false, async () => {
      const jwt = await readJwt();
      if (!jwt) {
        set({ notice: actionFailureNotice("limit", UNAUTHORIZED) });
        return false;
      }
      const result = await updateWeeklyLimit(jwt, {
        weeklyLimit,
        expectedVersion: state.weeklyLimit?.version ?? 0,
      });
      if (!mounted) return false;
      if (result.ok) {
        set({
          weeklyLimit: { ...result.value.weeklyLimit, version: result.value.version },
          notice: {
            tone: "success",
            message:
              result.value.weeklyLimit.mode === "fixed"
                ? `Limite semanal atualizado para ${formatBRL(result.value.weeklyLimit.cents)}.`
                : "Limite semanal atualizado: sem teto interno da DaKasa.",
          },
        });
        announce();
        return true;
      }
      if (result.failure.code === "state_conflict") {
        const reloaded = await refreshReadiness(jwt);
        set({
          notice: {
            tone: "warning",
            message: reloaded ? WEEKLY_LIMIT_CONFLICT_RELOADED : WEEKLY_LIMIT_CONFLICT_STALE,
          },
        });
        return false;
      }
      set({ notice: actionFailureNotice("limit", result.failure) });
      return false;
    });
  }

  return {
    mount() {
      mounted = true;
      void load();
    },
    unmount() {
      mounted = false;
      generation += 1;
      stopPolling();
    },
    start,
    cancel,
    saveWeeklyLimit,
    retry: () => load(),
    dismissNotice: () => set({ notice: null }),
    getState: () => state,
  };
}
